import React, { useEffect, useRef, useState } from 'react';

interface LocationData {
  city: string;
  state: string;
  country: string;
  postcode: string;
  coordinates: {
    type: 'Point';
    coordinates: [number, number]; // [lng, lat]
  };
}

interface LocationPickerProps {
  value: string;
  onChange: (value: string, locationData: any) => void;
  placeholder?: string;
}

declare global {
  interface Window {
    google: any;
    initAutocomplete: () => void;
  }
}

export const LocationPicker: React.FC<LocationPickerProps> = ({
  value,
  onChange,
  placeholder = 'Start typing a city...',
}) => {
  const inputRef = useRef<HTMLInputElement>(null);
  const autocompleteRef = useRef<any>(null);
  const [postcode, setPostcode] = useState('');
  const [coordinates, setCoordinates] = useState<[number, number]>([0, 0]);
  const [locationInfo, setLocationInfo] = useState<{
    city: string;
    state: string;
    country: string;
  }>({ city: '', state: '', country: '' });

  useEffect(() => {
    const loadGoogleMaps = () => {
      if (window.google && window.google.maps) {
        initAutocomplete();
        return;
      }

      if (!document.getElementById('google-maps-script')) {
        const script = document.createElement('script');
        script.id = 'google-maps-script';
        script.src =
          'https://maps.googleapis.com/maps/api/js?key=AIzaSyCwgVmtYRMNGry-rv8l91qgW-OfXOaoqCU&libraries=places';
        script.async = true;
        script.defer = true;
        script.onload = () => {
          if (window.google && window.google.maps) {
            initAutocomplete();
          }
        };
        document.head.appendChild(script);
      }
    };

    const initAutocomplete = () => {
      if (!inputRef.current) return;

      autocompleteRef.current = new window.google.maps.places.Autocomplete(
        inputRef.current,
        {
          types: ['(cities)'],
          fields: ['address_components', 'geometry'],
        },
      );

      autocompleteRef.current.addListener('place_changed', () => {
        const place = autocompleteRef.current.getPlace();

        if (!place.geometry) return;

        const components = place.address_components;
        const getComp = (type: string) =>
          (components.find((c: any) => c.types.includes(type)) || {})
            .long_name || '';

        const city =
          getComp('locality') ||
          getComp('sublocality') ||
          getComp('postal_town');
        const state = getComp('administrative_area_level_1');
        const country = getComp('country');
        const pc = getComp('postal_code');

        const lng = place.geometry.location.lng();
        const lat = place.geometry.location.lat();

        setLocationInfo({ city, state, country });
        setCoordinates([lng, lat]);
        if (pc) setPostcode(pc);

        const data: LocationData = {
          city,
          state,
          country,
          postcode: pc || postcode,
          coordinates: {
            type: 'Point',
            coordinates: [lng, lat],
          },
        };

        onChange(`${city}, ${country}`, data);
      });
    };

    loadGoogleMaps();
  }, []);

  const buildData = (
    info: { city: string; state: string; country: string },
    pc: string,
  ): LocationData => ({
    ...info,
    postcode: pc,
    coordinates: { type: 'Point', coordinates },
  });

  const handleFieldChange = (
    field: 'city' | 'state' | 'country',
    val: string,
  ) => {
    const updated = { ...locationInfo, [field]: val };
    setLocationInfo(updated);
    onChange(
      `${updated.city}, ${updated.country}`,
      buildData(updated, postcode),
    );
  };

  const handlePostcodeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newPostcode = e.target.value;
    setPostcode(newPostcode);
    onChange(
      `${locationInfo.city}, ${locationInfo.country}`,
      buildData(locationInfo, newPostcode),
    );
  };

  return (
    <div className='space-y-4 w-full'>
      <div className='relative group/input'>
        <input
            ref={inputRef}
            type='text'
            placeholder={placeholder}
            defaultValue={value}
            className='w-full px-4 py-2.5 rounded-xl border border-gray-200 focus:border-emerald-500 focus:ring-4 focus:ring-emerald-50 outline-none transition-all text-sm font-bold text-gray-900 pl-10'
            autoComplete='off'
        />
        <div className='absolute left-3.5 top-1/2 -translate-y-1/2 text-gray-300'>
          <svg
              className='w-4 h-4'
              fill='none'
              stroke='currentColor'
              viewBox='0 0 24 24'
          >
            <path
                strokeLinecap='round'
                strokeLinejoin='round'
                strokeWidth={2.5}
                d='M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z'
            />
            <path
                strokeLinecap='round'
                strokeLinejoin='round'
                strokeWidth={2.5}
                d='M15 11a3 3 0 11-6 0 3 3 0 016 0z'
            />
          </svg>
        </div>
      </div>

      <div className='grid grid-cols-2 gap-3'>
        <div className='space-y-1'>
          <label className='text-[10px] font-bold text-gray-400 uppercase tracking-wider ml-1'>
            City
          </label>
          <input
              type='text'
              value={locationInfo.city}
              placeholder='Enter city'
              onChange={(e) => handleFieldChange('city', e.target.value)}
              className='w-full px-3 py-2 rounded-lg bg-white border border-gray-200 focus:border-emerald-500 focus:ring-2 focus:ring-emerald-50 text-sm font-semibold text-gray-900 outline-none transition-all'
          />
        </div>
        <div className='space-y-1'>
          <label className='text-[10px] font-bold text-gray-400 uppercase tracking-wider ml-1'>
            State
          </label>
          <input
              type='text'
              value={locationInfo.state}
              placeholder='Enter state'
              onChange={(e) => handleFieldChange('state', e.target.value)}
              className='w-full px-3 py-2 rounded-lg bg-white border border-gray-200 focus:border-emerald-500 focus:ring-2 focus:ring-emerald-50 text-sm font-semibold text-gray-900 outline-none transition-all'
          />
        </div>
      </div>

      <div className='grid grid-cols-2 gap-3'>
        <div className='space-y-1'>
          <label className='text-[10px] font-bold text-gray-400 uppercase tracking-wider ml-1'>
            Country
          </label>
          <input
              type='text'
              value={locationInfo.country}
              placeholder='Enter country'
              onChange={(e) => handleFieldChange('country', e.target.value)}
              className='w-full px-3 py-2 rounded-lg bg-white border border-gray-200 focus:border-emerald-500 focus:ring-2 focus:ring-emerald-50 text-sm font-semibold text-gray-900 outline-none transition-all'
          />
        </div>
        <div className='space-y-1'>
          <label className='text-[10px] font-bold text-gray-400 uppercase tracking-wider ml-1'>
            Postal Code
          </label>
          <input
              type='text'
              placeholder='Enter manually'
              value={postcode}
              onChange={handlePostcodeChange}
              className='w-full px-3 py-2 rounded-lg bg-white border border-gray-200 focus:border-emerald-500 focus:ring-2 focus:ring-emerald-50 text-sm font-semibold text-gray-900 outline-none transition-all'
          />
        </div>
      </div>
    </div>
  );
};
